import React from 'react';
import { useModalClose } from '../../hooks/useModalClose';
import { URLManager } from '../../lib/url/URLManager';
import { ProjectConfig } from '../../types';
import { closeMenuAndUpdateUrl } from '../../lib/url/menuUtils';

interface InfoScreenProps {
  onClose: () => void;
  project?: { identifier: string; config: ProjectConfig };
  isOpen: boolean;
}

export const InfoScreen: React.FC<InfoScreenProps> = ({ onClose, project, isOpen }) => {
  const urlManager = React.useRef(URLManager.getInstance());
  const modalRef = useModalClose({ 
    onClose: () => {
      if (project) {
        closeMenuAndUpdateUrl(project.identifier);
      }
      onClose();
    }, 
    project,
    view: 'help',
    onSave: async () => {
      // InfoScreen doesn't have any data to save
      return Promise.resolve();
    }
  });

  // Update URL when menu opens
  React.useEffect(() => {
    if (isOpen && project) {
      urlManager.current.updateURL(
        project.identifier,
        'network',
        'help',
        'auto',
        'help_screen_open'
      );
    }
  }, [isOpen, project]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
      }}
    >
      <div
        ref={modalRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '1040px',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          margin: '8px',
          width: 'calc(100% - 16px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>
          {`
            kbd {
              background-color: #f3f4f6;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              padding: 2px 6px;
              font-size: 12px;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
              box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
              margin: 0 2px;
            }
          `}
        </style>
        <button
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            border: 'none',
            background: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px',
          }}
          onClick={onClose}
        >
          ×
        </button>

        <h2 style={{ marginTop: 0, marginBottom: '24px', fontWeight: 'bold' }}>How to Use the Network Editor</h2>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '24px',
          maxWidth: '100%'
        }}>
          <section>
            <h3 className="font-bold mb-2">Basic Navigation</h3>
            <ul>
              <li>Pan: Click and drag on empty space</li>
              <li>Zoom: Use mouse wheel or trackpad gestures</li>
              <li>Reset view: Double-click on empty space</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Creating Cities</h3>
            <ul>
              <li>Click on any empty space to create a new city</li>
              <li>Cities are automatically numbered</li>
              <li>Click on a city's label to edit its properties</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Creating Routes</h3>
            <ul>
              <li>Click one city to select it (it will highlight)</li>
              <li>Click another city to create a route between them</li>
              <li>Click a route's label to edit its properties</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Editing</h3>
            <ul>
              <li>Drag cities to move them</li>
              <li>Click city/route labels to open context menu</li>
              <li>Use context menu to edit properties or delete</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Population Input</h3>
            <ul>
              <li>Manual: Enter population directly in the city menu</li>
              <li>Automatic: Enter latitude/longitude to fetch population within 30km radius</li>
              <li>Node size reflects population in the visualization</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Data Storage</h3>
            <ul>
              <li>All project data is stored locally in your web browser</li>
              <li>No data is sent to or stored in the cloud</li>
              <li>Data persists between sessions unless you clear browser data</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold mb-2">Route Colors</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', backgroundColor: '#A8E6A3', display: 'inline-block' }} />
                <span>High passengers per km</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', backgroundColor: '#4A90E2', display: 'inline-block' }} />
                <span>Medium</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', backgroundColor: '#7D3C98', display: 'inline-block' }} />
                <span>Low</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', backgroundColor: '#666', display: 'inline-block' }} />
                <span>No passenger data</span>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-bold mb-2">Route Types</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', backgroundColor: '#666', display: 'inline-block' }} />
                <span>Domestic</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', borderTop: '5px dotted #666', display: 'inline-block' }} />
                <span>Border crossing without customs control</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: '50px', height: '5px', borderTop: '5px dashed #666', display: 'inline-block' }} />
                <span>Border crossing with customs control</span>
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-bold mb-2">Keyboard Shortcuts</h3>
            <ul>
              <li><kbd>Esc</kbd> Deselect city / Close menus</li>
              <li><kbd>⌘</kbd><kbd>Z</kbd> / <kbd>Ctrl</kbd><kbd>Z</kbd> Undo</li>
              <li><kbd>⌘</kbd><kbd>⇧</kbd><kbd>Z</kbd> / <kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>Z</kbd> Redo</li>
              <li><kbd>⌘</kbd> + Click / <kbd>Ctrl</kbd> + Click: Delete city or route</li>
              <li><kbd>H</kbd> Open help screen</li>
              <li><kbd>F</kbd> Fit and center network</li>
              <li><kbd>R</kbd> Open routes menu</li>
              <li><kbd>C</kbd> Open cities menu</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}; 